// src/components/DocTrail.js
// Document trail (QT → SO → SL) for a bill — each step opens that bill's details,
// its number can be copied, and DocCompareButton compares the items across the steps
import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import {
  Box, Button, Dialog, DialogContent, DialogTitle, FormControlLabel, IconButton, Switch,
  Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow,
  Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import dayjs from 'dayjs';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import api from '../adapters/api';

// Bill type colors — same as the status chips/tabs in OrderList. They only say which kind
// of bill it is, never good/bad.
export const TRAIL_STATUS = {
  QT: { label: 'ใบเสนอราคา', bg: '#e3f2fd', color: '#1565c0' },
  SO: { label: 'ใบสั่งขาย', bg: '#e8f5e9', color: '#2e7d32' },
  SL: { label: 'ใบกำกับภาษี', bg: '#fce4ec', color: '#c62828' },
};

// Difference colors — kept off the blue/green/red the bill types own, so "SO green"
// isn't read as "OK" and "SL red" isn't read as "error"
const DIFF = {
  changed: { color: '#c2410c', bg: '#ffedd5', bar: '#f97316' }, // orange
  added:   { color: '#2e7d32', bg: '#e8f5e9' }, // green (same as 'same' / SO — only marks a new line here)
  removed: { color: '#d32f2f', bg: '#fdecea' }, // red, shown as struck-through text
  same:    { color: '#2e7d32', bg: '#e8f5e9' }, // green — fine to share with SO here, it only marks "no change"
};
dayjs.extend(buddhistEra);

const EPS = 0.005;

const fmt2 = (n) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(n) || 0);
const fmtQty = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(parseFloat(n) || 0);
const fmtDate = (d) => (d ? dayjs(d).format('DD/MM/BBBB') : '—');
const isUsed = (doc) => Number(doc.used_status) === 1 && doc.status !== 'SL';

// rows: null while the first load is pending, [] when the bill wasn't found.
// Keeps the previous rows while refetching so the trail doesn't flicker when moving along it.
export function useDocTrail(docNo) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!docNo) { setRows(null); return; }
    let cancelled = false;
    api.get(`/api/orders/${encodeURIComponent(docNo)}/trail`)
      .then(({ data }) => { if (!cancelled) setRows(Array.isArray(data) ? data : []); })
      .catch(err => {
        if (err?.response?.status !== 404) console.error('doc trail fetch error:', err);
        if (!cancelled) setRows([]);
      });
    return () => { cancelled = true; };
  }, [docNo]);
  return rows;
}

// navigator.clipboard is missing on plain-http origins — fall back to execCommand like Checkout.js
function copyText(text) {
  const fallback = () => {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.cssText = 'position:fixed;opacity:0';
    document.body.appendChild(el);
    el.focus();
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  };
  if (navigator.clipboard) return navigator.clipboard.writeText(text).catch(fallback);
  fallback();
  return Promise.resolve();
}

export function CopyDocNoButton({ docNo, size = 14 }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  return (
    <Tooltip title={copied ? 'คัดลอกแล้ว' : 'คัดลอกเลขที่บิล'} arrow>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); copyText(docNo).then(() => setCopied(true)); }}
        sx={{ p: 0.4, color: copied ? 'success.main' : 'text.secondary' }}
      >
        {copied ? <CheckIcon sx={{ fontSize: size }} /> : <ContentCopyIcon sx={{ fontSize: size }} />}
      </IconButton>
    </Tooltip>
  );
}

function Badge({ children, color, bg }) {
  return (
    <Box component="span" sx={{
      fontSize: 10.5, fontWeight: 700, lineHeight: 1, px: 0.75, py: 0.4, borderRadius: 10,
      color, bgcolor: bg, whiteSpace: 'nowrap',
    }}>
      {children}
    </Box>
  );
}

function TrailCard({ doc, isCurrent, clickable, currentLabel, onOpen }) {
  const cfg = TRAIL_STATUS[doc.status];
  const card = (
    <Box
      onClick={clickable ? () => onOpen(doc.doc_no) : undefined}
      sx={{
        position: 'relative',
        p: 1.25, pl: 1.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: isCurrent ? cfg.color : 'divider',
        borderLeft: `4px solid ${cfg.color}`,
        bgcolor: isCurrent ? cfg.bg : 'background.paper',
        boxShadow: isCurrent ? `0 0 0 1px ${cfg.color}30` : 'none',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'box-shadow .15s, border-color .15s',
        minWidth: 0,
        '&:hover': clickable ? { borderColor: cfg.color, boxShadow: 2 } : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
        <Typography variant="caption" sx={{ color: cfg.color, fontWeight: 700, letterSpacing: 0.3 }}>
          {cfg.label}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {isCurrent && <Badge color="#fff" bg={cfg.color}>{currentLabel}</Badge>}
        {isUsed(doc) && <Badge color={DIFF.removed.color} bg={DIFF.removed.bg}>ใช้แล้ว</Badge>}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 13.5, minWidth: 0 }}>
          {doc.doc_no}
        </Typography>
        <CopyDocNoButton docNo={doc.doc_no} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mt: 0.25 }}>
        <Typography variant="caption" color="text.secondary" noWrap>{fmtDate(doc.doc_date)}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
          {fmt2(doc.total_amount)} ฿
        </Typography>
      </Box>
    </Box>
  );
  return clickable
    ? <Tooltip title="คลิกเพื่อดูรายละเอียดบิล" arrow placement="top" enterDelay={400}>{card}</Tooltip>
    : card;
}

// Amount change vs the bill(s) this one came from: a merged bill vs the sum of its sources,
// sibling bills that split one source show "แบ่งส่ง" when the parts add up
function StageDiff({ result, hideSame = false }) {
  if (!result) return null;
  const base = { fontWeight: 600, whiteSpace: 'nowrap', lineHeight: 1.2 };
  if (result.split) return <Typography variant="caption" sx={{ ...base, color: 'text.secondary' }}>แบ่งส่ง</Typography>;
  const same = Math.abs(result.diff) < EPS;
  if (same && hideSame) return null;
  return (
    <Typography variant="caption" sx={{
      ...base,
      // up or down is just "changed" — neither is good or bad by itself
      color: same ? 'text.secondary' : DIFF.changed.color,
    }}>
      {same ? '✓ ยอดเท่าเดิม' : `${result.diff > 0 ? '▲ +' : '▼ '}${fmt2(result.diff)}`}
    </Typography>
  );
}

// The trail as chains of bills (paths) from ref_docs, as recorded on the bills — a repeat order
// copied from an earlier SL still points at the QT, so it shows as its own path from that QT.
// One path when nothing splits or merges.
function trailGraph(rows) {
  const inTrail = new Set(rows.map(r => r.doc_no));
  const parentsOf = Object.fromEntries(rows.map(r => [
    r.doc_no, (r.ref_docs || []).filter(p => inTrail.has(p) && p !== r.doc_no),
  ]));
  const parentOf = Object.fromEntries(rows.map(r => [r.doc_no, parentsOf[r.doc_no][0] || null]));
  const byNo = Object.fromEntries(rows.map(r => [r.doc_no, r]));
  const totalOf = no => parseFloat(byNo[no]?.total_amount) || 0;
  const diffs = Object.fromEntries(rows.map(r => [r.doc_no, additiveDiff(r.doc_no, rows, parentOf, parentsOf, totalOf)]));
  const paths = buildPaths(rows, parentsOf).map(p => p.map(no => byNo[no]));
  return { paths, diffs };
}

// How many separate paths the trail has (the search banner grows by one line per extra path)
export function trailPathCount(rows) {
  return rows && rows.length ? trailGraph(rows).paths.length : 0;
}

function PathLabel({ index }) {
  return (
    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}>
      สายที่ {index + 1}
    </Typography>
  );
}

// One line per bill — for the narrow detail drawers
function CompactRow({ doc, diff, first, currentDocNo, disableCurrent, onOpen }) {
  const cfg = TRAIL_STATUS[doc.status];
  const isCurrent = doc.doc_no === currentDocNo;
  const clickable = Boolean(onOpen) && !(isCurrent && disableCurrent);
  return (
    <Tooltip
      arrow
      placement="left"
      enterDelay={400}
      title={`${cfg.label} · ${fmtDate(doc.doc_date)}${clickable ? ' · คลิกเพื่อดูรายละเอียด' : ''}`}
    >
      <Box
        onClick={clickable ? () => onOpen(doc.doc_no) : undefined}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.75,
          px: 1, py: 0.5, minHeight: 34,
          borderTop: first ? 'none' : '1px solid', borderColor: 'divider',
          boxShadow: `inset 3px 0 0 ${cfg.color}`,
          bgcolor: isCurrent ? cfg.bg : 'transparent',
          cursor: clickable ? 'pointer' : 'default',
          '&:hover': clickable ? { bgcolor: `${cfg.color}0f` } : undefined,
        }}
      >
        <Box component="span" sx={{
          flex: '0 0 auto', width: 26, textAlign: 'center',
          fontSize: 10.5, fontWeight: 800, lineHeight: '18px', borderRadius: 0.75,
          color: cfg.color, bgcolor: isCurrent ? '#fff' : cfg.bg,
        }}>
          {doc.status}
        </Box>
        <Typography variant="body2" noWrap sx={{
          fontFamily: 'monospace', fontSize: 13, minWidth: 0,
          fontWeight: isCurrent ? 800 : 600, color: isCurrent ? cfg.color : 'text.primary',
        }}>
          {doc.doc_no}
        </Typography>
        <CopyDocNoButton docNo={doc.doc_no} size={13} />
        {isUsed(doc) && <Badge color="#616161" bg="#eeeeee">ใช้แล้ว</Badge>}
        <Box sx={{ flex: 1 }} />
        <StageDiff result={diff} hideSame />
        <Typography variant="caption" noWrap color="text.secondary" sx={{ flex: '0 0 auto', fontSize: 11.5 }}>
          {doc.doc_date ? dayjs(doc.doc_date).format('DD/MM/BB') : ''}
        </Typography>
        <Typography variant="caption" noWrap sx={{ fontWeight: 700, color: 'text.primary', flex: '0 0 auto', minWidth: 72, textAlign: 'right' }}>
          {fmt2(doc.total_amount)}
        </Typography>
      </Box>
    </Tooltip>
  );
}

// A bill as a small inline chip — one line per path when the trail splits or merges
function PathChip({ doc, isCurrent, clickable, onOpen }) {
  const cfg = TRAIL_STATUS[doc.status];
  return (
    <Tooltip arrow placement="top" enterDelay={400}
      title={`${cfg.label}${isUsed(doc) ? ' · ใช้แล้ว' : ''}${clickable ? ' · คลิกเพื่อดูรายละเอียด' : ''}`}>
      <Box
        onClick={clickable ? () => onOpen(doc.doc_no) : undefined}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.75, flex: '0 0 auto',
          px: 1, py: 0.5, borderRadius: 1.5,
          border: '1px solid', borderColor: isCurrent ? cfg.color : 'divider',
          boxShadow: `inset 3px 0 0 ${cfg.color}`,
          bgcolor: isCurrent ? cfg.bg : 'background.paper',
          cursor: clickable ? 'pointer' : 'default',
          '&:hover': clickable ? { borderColor: cfg.color } : undefined,
        }}
      >
        <Box component="span" sx={{ fontSize: 10.5, fontWeight: 800, color: cfg.color }}>{doc.status}</Box>
        <Typography variant="body2" noWrap sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: isCurrent ? 800 : 600 }}>
          {doc.doc_no}
        </Typography>
        <CopyDocNoButton docNo={doc.doc_no} size={13} />
        {isUsed(doc) && <Badge color="#616161" bg="#eeeeee">ใช้แล้ว</Badge>}
        <Typography variant="caption" color="text.secondary" noWrap>
          {doc.doc_date ? dayjs(doc.doc_date).format('DD/MM/BB') : ''}
        </Typography>
        <Typography variant="caption" noWrap sx={{ fontWeight: 700 }}>{fmt2(doc.total_amount)}</Typography>
      </Box>
    </Tooltip>
  );
}

// Bills every path shares at the start (e.g. the QT a split came from) and at the end (e.g. the
// SL several SOs were merged into) are shown once; only the part in between splits into paths.
function forkOf(paths) {
  const minLen = Math.min(...paths.map(p => p.length));
  const sameAt = (fromStart, i) => paths.every(p => (fromStart ? p[i] : p[p.length - 1 - i]) === (fromStart ? paths[0][i] : paths[0][paths[0].length - 1 - i]));
  let pre = 0;
  while (pre < minLen && sameAt(true, pre)) pre++;
  let suf = 0;
  while (suf < minLen - pre && sameAt(false, suf)) suf++;
  return {
    prefix: paths[0].slice(0, pre),
    branches: paths.map(p => p.slice(pre, p.length - suf)),
    suffix: paths[0].slice(paths[0].length - suf),
  };
}

// currentDocNo is highlighted; with disableCurrent it can't be clicked (it's the bill already open).
// Drawn path by path: each arrow compares a bill with the bill it actually came from, so a
// split, a merge or a repeat order isn't summed into one step.
export default function DocTrail({ rows, currentDocNo, disableCurrent = false, onOpen, orientation = 'vertical' }) {
  if (!rows || rows.length === 0) return null;

  const { paths, diffs } = trailGraph(rows);
  const multi = paths.length > 1;
  const fork = multi ? forkOf(paths) : { prefix: paths[0], branches: [], suffix: [] };
  // a split delivery shares its source bill once; any other fork (e.g. a repeat order that still
  // points at the same QT) repeats the source on every path so each line reads on its own
  const sharedSplit = fork.branches.every(b => b.length && diffs[b[0].doc_no]?.split);
  const { prefix, branches, suffix } = multi && fork.prefix.length && !sharedSplit
    ? { ...fork, prefix: [], branches: fork.branches.map(b => [...fork.prefix, ...b]) }
    : fork;
  const currentLabel = disableCurrent ? 'กำลังดู' : 'บิลที่ค้นหา';
  const isClickable = doc => Boolean(onOpen) && !(doc.doc_no === currentDocNo && disableCurrent);

  const card = doc => (
    <TrailCard
      key={doc.doc_no}
      doc={doc}
      isCurrent={doc.doc_no === currentDocNo}
      currentLabel={currentLabel}
      clickable={isClickable(doc)}
      onOpen={onOpen}
    />
  );

  if (orientation === 'compact') {
    // first: whether the row opens the list (diff is shown on every row that has a source)
    const list = (docs, startsTrail) => (
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
        {docs.map((doc, i) => (
          <CompactRow
            key={doc.doc_no}
            doc={doc}
            diff={startsTrail && i === 0 ? null : diffs[doc.doc_no]}
            first={i === 0}
            currentDocNo={currentDocNo}
            disableCurrent={disableCurrent}
            onOpen={onOpen}
          />
        ))}
      </Box>
    );
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {prefix.length > 0 && list(prefix, true)}
        {branches.map((b, bi) => (
          <Box key={bi} sx={{ ml: prefix.length ? 1.5 : 0, pl: prefix.length ? 1 : 0, borderLeft: prefix.length ? '2px solid' : 'none', borderColor: 'divider' }}>
            <Box sx={{ mb: 0.25 }}><PathLabel index={bi} /></Box>
            {b.length > 0 ? list(b, !prefix.length) : <Typography variant="caption" color="text.disabled">—</Typography>}
          </Box>
        ))}
        {suffix.length > 0 && list(suffix, false)}
      </Box>
    );
  }

  // search banner: the same small chips for one path or several, so it reads the same either way
  if (orientation === 'horizontal') {
    const arrow = (doc, size = 18) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '0 0 auto' }}>
        <ArrowForwardIcon sx={{ fontSize: size, color: 'text.disabled' }} />
        <StageDiff result={diffs[doc.doc_no]} hideSame />
      </Box>
    );
    const chip = doc => (
      <PathChip doc={doc} isCurrent={doc.doc_no === currentDocNo} clickable={isClickable(doc)} onOpen={onOpen} />
    );
    // a run of bills joined by arrows; lead: draw the arrow into the first bill too
    const chain = (docs, lead) => docs.map((doc, i) => (
      <React.Fragment key={doc.doc_no}>
        {(i > 0 || lead) && arrow(doc)}
        {chip(doc)}
      </React.Fragment>
    ));
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflowX: 'auto' }}>
        {prefix.length > 0 && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '0 0 auto' }}>{chain(prefix, false)}</Box>}
        {branches.length > 0 && <Box sx={{
          display: 'flex', flexDirection: 'column', gap: 0.75, flex: '0 0 auto',
          // bracket lines show where the paths split from / join back into the shared bills
          borderLeft: prefix.length ? '2px solid' : 'none',
          borderRight: suffix.length ? '2px solid' : 'none',
          borderColor: 'divider', px: 1,
        }}>
          {branches.map((b, bi) => (
            <Box key={bi} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 44, flex: '0 0 auto' }}><PathLabel index={bi} /></Box>
              {chain(b, prefix.length > 0)}
            </Box>
          ))}
        </Box>}
        {suffix.length > 0 && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '0 0 auto' }}>{chain(suffix, true)}</Box>}
      </Box>
    );
  }

  const step = doc => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 30, pl: 2 }}>
      <Box sx={{ width: 2, alignSelf: 'stretch', bgcolor: 'divider', borderRadius: 1 }} />
      <ArrowDownwardIcon sx={{ fontSize: 15, color: 'text.disabled', ml: -0.5 }} />
      <StageDiff result={diffs[doc.doc_no]} />
    </Box>
  );
  const column = (docs, lead) => docs.map((doc, i) => (
    <React.Fragment key={doc.doc_no}>
      {(i > 0 || lead) && step(doc)}
      {card(doc)}
    </React.Fragment>
  ));
  return (
    <Box>
      {column(prefix, false)}
      {branches.map((b, bi) => (
        <Box key={bi} sx={{ mt: 1, ml: prefix.length ? 1.5 : 0, pl: prefix.length ? 1 : 0, borderLeft: prefix.length ? '2px solid' : 'none', borderColor: 'divider' }}>
          <PathLabel index={bi} />
          {column(b, prefix.length > 0)}
        </Box>
      ))}
      {suffix.length > 0 && <Box sx={{ mt: 1 }}>{column(suffix, true)}</Box>}
    </Box>
  );
}


// ---------- Header + item comparison across the trail ----------

const joinText = (...parts) => parts.map(p => (p || '').toString().trim()).filter(Boolean).join(' ');

// Delivery place from a GET /orders/:docNo header.
// ERP addresses often already spell out ต./อ./จ. — only append the parts that are missing
export function deliveryText(h) {
  if (!h) return '';
  const addr = (h.delivery_address || '').trim();
  const part = (prefix, name) => (name && !addr.includes(name) ? `${prefix}${name}` : '');
  return joinText(addr, part('ต.', h.delivery_tambon), part('อ.', h.delivery_amper), part('จ.', h.delivery_province));
}

function deliveryCoord(h) {
  const lat = parseFloat(h?.delivery_latitude);
  const lng = parseFloat(h?.delivery_longitude);
  return lat && lng ? { lat, lng } : null;
}

// 6 decimals ≈ 10 cm — enough to tell a moved pin from float noise
const coordText = c => (c ? `${c.lat.toFixed(6)}, ${c.lng.toFixed(6)}` : '');

// Google Maps at the pin, or a search for the address when the place has no coordinates
export function deliveryMapUrl(h) {
  const c = deliveryCoord(h);
  const q = c ? `${c.lat},${c.lng}` : deliveryText(h);
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : '';
}

// Address + "เปิดแผนที่" link — for the bill drawers
export function DeliveryPlace({ header }) {
  const text = deliveryText(header);
  const url = deliveryMapUrl(header);
  const c = deliveryCoord(header);
  if (!text && !url) return <Typography variant="body2">—</Typography>;
  return (
    <Box>
      {text && <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{text}</Typography>}
      {url && <MapLink url={url} label={c ? coordText(c) : 'ค้นหาในแผนที่'} />}
    </Box>
  );
}

function MapLink({ url, label }) {
  return (
    <Box
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => e.stopPropagation()}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.25, mt: 0.25,
        fontSize: 12, color: 'primary.main', textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      <PlaceOutlinedIcon sx={{ fontSize: 14 }} />
      {label}
    </Box>
  );
}

// Header fields from GET /orders/:docNo compared bill-to-bill; values are formatted
// first so e.g. "0" and "0.0" count as equal
const HEADER_FIELDS = [
  // sub: a faded second line under the value (still part of the comparison)
  { label: 'ลูกค้า', get: h => h.customer_name || '', sub: h => h.cust_code || '' },
  { label: 'พนักงานขาย', get: h => h.sale_name || h.sale_code || '' },
  { label: 'สาขา', get: h => h.branch_code || '' },
  { label: 'วิธีรับสินค้า', get: h => (Number(h.send_type) === 1 ? 'จัดส่ง' : Number(h.send_type) === 0 ? 'รับเอง' : '') },
  // coordinates are compared too (the sub line) — a pin moved with the same address text is a change.
  // Only when both bills have a pin: most SOs opened in the ERP carry none, which isn't a move.
  {
    label: 'สถานที่ส่ง', get: deliveryText, sub: h => coordText(deliveryCoord(h)), link: deliveryMapUrl,
    subChanged: (a, b) => Boolean(a && b) && a !== b,
  },
  { label: 'เครดิต', get: h => (h.credit_day !== null && h.credit_day !== undefined ? `${h.credit_day} วัน` : '') },
  { label: 'ส่วนลด', get: h => fmt2(h.discount) },
  { label: 'ค่าขนส่ง', get: h => fmt2(h.shipping_cost) },
  // num: additive field — bills splitting one source between them are fine if the parts add up
  { label: 'น้ำหนักรวม', get: h => `${fmt2(h.total_weight)} กก.`, num: h => parseFloat(h.total_weight) || 0 },
  { label: 'หมายเหตุ', get: h => (h.remark || '').trim() },
];

// Additive values (total, weight) vs the source: a bill merged from several sources is compared
// with their sum, and sibling bills splitting one source are fine when the parts add up.
// Returns null (no source), { split: true } or { diff }.
function additiveDiff(docNo, docs, parentOf, parentsOf, valueOf) {
  const parents = parentsOf[docNo] || [];
  if (!parents.length) return null;
  if (parents.length > 1) return { diff: valueOf(docNo) - parents.reduce((a, p) => a + valueOf(p), 0) };
  const p = parents[0];
  const sibs = docs.filter(d => (parentsOf[d.doc_no] || []).length === 1 && parentOf[d.doc_no] === p);
  if (sibs.length > 1 && sameNum(sibs.reduce((a, d) => a + valueOf(d.doc_no), 0), valueOf(p))) return { split: true };
  return { diff: valueOf(docNo) - valueOf(p) };
}

// Each bill's header is compared with its source bill (parentOf), not with the column on its left
function buildHeaderRows(docs, headersByDoc, parentOf, parentsOf) {
  return HEADER_FIELDS.map(f => {
    const val = no => (headersByDoc[no] ? f.get(headersByDoc[no]) : '');
    const sub = no => (f.sub && headersByDoc[no] ? f.sub(headersByDoc[no]) : '');
    const values = docs.map(d => val(d.doc_no));
    const subs = docs.map(d => sub(d.doc_no));
    const links = docs.map(d => (f.link && headersByDoc[d.doc_no] ? f.link(headersByDoc[d.doc_no]) : ''));
    // the value and its sub line are boxed separately, so e.g. a moved pin shows on the coordinates
    const valueChanged = docs.map((d, i) => {
      const p = parentOf[d.doc_no];
      if (!p) return false;
      if (f.num) {
        const r = additiveDiff(d.doc_no, docs, parentOf, parentsOf, no => (headersByDoc[no] ? f.num(headersByDoc[no]) : 0));
        return Boolean(r && !r.split && Math.abs(r.diff) > EPS);
      }
      return values[i] !== val(p);
    });
    const subDiffers = f.subChanged || ((a, b) => a !== b);
    const subChanged = docs.map((d, i) => Boolean(parentOf[d.doc_no]) && !f.num && subDiffers(subs[i], sub(parentOf[d.doc_no])));
    const changed = docs.map((d, i) => valueChanged[i] || subChanged[i]);
    return { label: f.label, values, subs, links, valueChanged, subChanged, changed, differs: changed.some(Boolean) };
  });
}

const sameNum = (a, b) => Math.abs(a - b) <= EPS;

// Fallback pairing for lines that don't carry ref_row, most specific first. Swapping lines
// doesn't count as a change, and a renamed item or a changed unit still pairs with its old line.
const MATCH_PASSES = [
  (a, b) => a.key === b.key && a.name === b.name && sameNum(a.qty, b.qty) && sameNum(a.price, b.price),
  (a, b) => a.key === b.key && a.name === b.name,
  (a, b) => a.key === b.key && sameNum(a.price, b.price),
  (a, b) => a.key === b.key,
  (a, b) => a.item_code === b.item_code && a.name === b.name, // unit changed
  (a, b) => a.item_code === b.item_code,
];

/**
 * Pairs every line with the line it was pulled from (its "source"), then groups the lines into
 * rows — one row per original line and everything that came from it. Each cell is compared with
 * its own source line, so sibling bills (one QT split into two SOs, a bill repeated twice) and
 * bills merged from several sources compare correctly.
 */
function buildLineage(docs, detailsByDoc) {
  const inTrail = new Set(docs.map(d => d.doc_no));
  const status = Object.fromEntries(docs.map(d => [d.doc_no, d.status]));
  const lines = {};      // doc_no -> lines
  const byLn = {};       // doc_no -> line_number -> line
  docs.forEach(doc => {
    lines[doc.doc_no] = (detailsByDoc[doc.doc_no] || []).map((d, i) => ({
      id: `${doc.doc_no}#${i}`,
      doc: doc.doc_no,
      ln: Number(d.line_number ?? i),
      order: i,
      key: `${d.item_code}|${d.unit_code}`,
      item_code: d.item_code,
      unit_code: d.unit_code,
      // name is per bill — the same item code can be renamed on a later bill
      name: (d.item_name || '').trim(),
      qty: parseFloat(d.qty) || 0,
      price: parseFloat(d.price) || 0,
      refDoc: inTrail.has(d.ref_doc_no) ? d.ref_doc_no : null,
      refRow: Number(d.ref_row ?? -1),
      source: null,
    }));
    byLn[doc.doc_no] = Object.fromEntries(lines[doc.doc_no].map(l => [l.ln, l]));
  });

  // the bill most of a bill's lines were pulled from — used for lines with no ref and for the header
  const parentOf = {};
  docs.forEach(doc => {
    const count = {};
    lines[doc.doc_no].forEach(l => { if (l.refDoc && l.refDoc !== doc.doc_no) count[l.refDoc] = (count[l.refDoc] || 0) + 1; });
    const best = Object.entries(count).sort((a, b) => b[1] - a[1])[0];
    parentOf[doc.doc_no] = best ? best[0] : (doc.ref_docs || []).find(r => inTrail.has(r) && r !== doc.doc_no) || null;
  });


  docs.forEach(doc => {
    const own = lines[doc.doc_no];
    const used = new Set();
    // 1) exact link: ref_doc_no + ref_row (= line_number in the source bill)
    own.forEach(l => {
      const s = l.refDoc && l.refRow >= 0 ? byLn[l.refDoc][l.refRow] : null;
      if (s && s.item_code === l.item_code && !used.has(s)) { l.source = s; used.add(s); }
    });
    // 2) no ref_row: pair inside the bill the line refers to (or the bill's main source)
    MATCH_PASSES.forEach(match => {
      own.forEach(l => {
        if (l.source) return;
        const target = l.refDoc || parentOf[doc.doc_no];
        if (!target || target === doc.doc_no) return;
        const s = lines[target].find(c => !used.has(c) && match(l, c));
        if (s) { l.source = s; used.add(s); }
      });
    });
  });

  // group into rows: a line with no source starts a row, the rest join their source's row
  const rows = [];
  const rowOf = new Map();
  docs.forEach((doc, di) => {
    lines[doc.doc_no].forEach(l => {
      let row = l.source && rowOf.get(l.source);
      if (!row) {
        row = { id: l.id, root: l, order: di * 10000 + l.order, cells: {} };
        rows.push(row);
      }
      rowOf.set(l, row);
      (row.cells[l.doc] ||= []).push(l);
    });
  });

  // children of each bill = bills holding at least one line pulled from it
  const childrenOf = {};
  docs.forEach(doc => {
    const srcs = new Set(lines[doc.doc_no].map(l => l.source && l.source.doc).filter(Boolean));
    if (parentOf[doc.doc_no]) srcs.add(parentOf[doc.doc_no]);
    srcs.forEach(p => { if (p !== doc.doc_no) (childrenOf[p] ||= new Set()).add(doc.doc_no); });
  });
  const pulled = new Set(docs.flatMap(d => lines[d.doc_no].map(l => l.source).filter(Boolean)));

  rows.forEach(row => {
    row.removedIn = {};   // doc_no -> the source line that bill dropped
    Object.values(row.cells).flat().forEach(l => {
      if (pulled.has(l)) return;
      (childrenOf[l.doc] || []).forEach(c => { if (!row.cells[c]) row.removedIn[c] = l; });
    });
  });

  // every bill a bill's lines came from (more than one = merged bill)
  const parentsOf = Object.fromEntries(docs.map(d => {
    const ps = new Set(lines[d.doc_no].map(l => l.source && l.source.doc).filter(x => x && x !== d.doc_no));
    if (!ps.size && parentOf[d.doc_no]) ps.add(parentOf[d.doc_no]);
    return [d.doc_no, [...ps]];
  }));
  return { rows: rows.sort((a, b) => a.order - b.order), parentOf, parentsOf, status };
}

// What happened to one line compared with its source line
function lineState(l, allLines) {
  const s = l.source;
  if (!s) return null;
  const unitChanged = l.unit_code !== s.unit_code;
  const priceChanged = !sameNum(l.price, s.price);
  const nameChanged = l.name !== s.name;
  // the source line split across sibling bills (same stage): fine as long as the parts add up
  const parts = allLines.filter(x => x.source === s && x.doc !== s.doc);
  const partsTotal = parts.reduce((a, x) => a + x.qty, 0);
  const split = parts.length > 1 && sameNum(partsTotal, s.qty);
  const qtyChanged = !split && !sameNum(l.qty, s.qty);
  return { qtyChanged: qtyChanged || unitChanged, priceChanged, nameChanged, split, sourceQty: s.qty };
}

function buildComparison(docs, detailsByDoc, headersByDoc) {
  const { rows: lineRows, parentOf, parentsOf } = buildLineage(docs, detailsByDoc);
  const rows = lineRows.map(row => {
    const all = Object.values(row.cells).flat();
    const rootIsNew = !row.root.source && Boolean(parentOf[row.root.doc]);
    let changed = false;
    const cells = {};
    docs.forEach(d => {
      const ls = row.cells[d.doc_no];
      if (ls) {
        cells[d.doc_no] = ls.map(l => {
          const st = l === row.root && rootIsNew ? { added: true } : lineState(l, all) || {};
          if (st.qtyChanged || st.priceChanged || st.nameChanged) changed = true;
          return { line: l, state: st };
        });
      } else if (row.removedIn[d.doc_no]) {
        cells[d.doc_no] = [{ line: row.removedIn[d.doc_no], state: { removed: true } }];
      }
    });
    const hasRemoved = Object.keys(row.removedIn).length > 0;
    // label tone: green = new line, red struck = dropped and never carried on, orange = changed
    const kind = rootIsNew ? 'added' : (hasRemoved && all.length === 1) ? 'removed' : (changed || hasRemoved) ? 'changed' : 'same';
    return {
      id: row.id, item_code: row.root.item_code, item_name: row.root.name, cells, kind,
      differs: kind !== 'same',
    };
  });
  // bill totals: vs the source bill; a clean split shows as แบ่งส่ง instead of a difference
  const totalOf = no => parseFloat((docs.find(d => d.doc_no === no) || {}).total_amount) || 0;
  const totals = Object.fromEntries(docs.map(d => [d.doc_no, additiveDiff(d.doc_no, docs, parentOf, parentsOf, totalOf)]));
  return { rows, headerRows: buildHeaderRows(docs, headersByDoc, parentOf, parentsOf), parentOf, parentsOf, totals };
}

// Every chain from a starting bill to a last bill (e.g. QT → SO#1 → SL and QT → SO#2 → SL).
// A trail that splits or merges has more than one; each is shown on its own tab.
function buildPaths(docs, parentsOf) {
  const children = {};
  docs.forEach(d => (parentsOf[d.doc_no] || []).forEach(p => (children[p] ||= []).push(d.doc_no)));
  const roots = docs.filter(d => !(parentsOf[d.doc_no] || []).length).map(d => d.doc_no);
  const paths = [];
  const walk = (no, path) => {
    const next = (children[no] || []).filter(c => !path.includes(c));
    if (!next.length) { paths.push([...path, no]); return; }
    next.forEach(c => walk(c, [...path, no]));
  };
  roots.forEach(r => walk(r, []));
  return paths.length ? paths : [docs.map(d => d.doc_no)];
}

// The comparison seen through the columns of one path: rows/labels/counts only reflect those bills
function viewOf(comparison, docs, visibleNos) {
  const visible = new Set(visibleNos);
  const idx = docs.map((d, i) => (visible.has(d.doc_no) ? i : -1)).filter(i => i >= 0);
  const vdocs = idx.map(i => docs[i]);
  const headerRows = comparison.headerRows.map(r => {
    const changed = idx.map(i => r.changed[i]);
    const pick = arr => idx.map(i => arr[i]);
    return {
      ...r, values: pick(r.values), subs: pick(r.subs), links: pick(r.links),
      valueChanged: pick(r.valueChanged), subChanged: pick(r.subChanged), changed, differs: changed.some(Boolean),
    };
  });
  const rows = comparison.rows.map(r => {
    const vis = vdocs.flatMap(d => r.cells[d.doc_no] || []);
    if (!vis.length) return null;
    const changed = vis.some(e => e.state.qtyChanged || e.state.priceChanged || e.state.nameChanged);
    const added = vis.some(e => e.state.added);
    const removed = vis.some(e => e.state.removed);
    const kind = changed ? 'changed'
      : added && !removed ? 'added'
      : removed && vis.filter(e => !e.state.removed).length <= 1 ? 'removed'
      : removed || added ? 'changed' : 'same';
    return { ...r, kind, differs: kind !== 'same' };
  }).filter(Boolean);
  return { vdocs, visible, headerRows, rows };
}

function Pill({ tone, children }) {
  return (
    <Box component="span" sx={{
      display: 'inline-block', fontSize: 11.5, fontWeight: 700, lineHeight: '20px', px: 1, borderRadius: 10,
      color: DIFF[tone].color, bgcolor: '#fff', border: `1px solid ${DIFF[tone].color}55`,
    }}>
      {children}
    </Box>
  );
}

// A value that changed (orange) or is new (green): soft box around just that value
function Changed({ children, tone = 'changed' }) {
  return (
    <Box component="span" sx={{
      display: 'inline-block', maxWidth: '100%', wordBreak: 'break-word',
      px: 1, py: 0.25, borderRadius: 1,
      bgcolor: DIFF[tone].bg, color: DIFF[tone].color, fontWeight: 700,
    }}>
      {children}
    </Box>
  );
}

const struck = { color: DIFF.removed.color, textDecoration: 'line-through', textDecorationColor: DIFF.removed.color };

// One line in a cell: quantity on top, unit price below
function LineValue({ line, state, fromDoc }) {
  const qty = `${fmtQty(line.qty)} ${line.unit_code}`;
  const price = `@ ${fmt2(line.price)}`;
  if (state.removed) {
    return (
      <Box>
        <Typography variant="body2" sx={struck}>{qty}</Typography>
        <Typography variant="body2" sx={struck}>{price}</Typography>
      </Box>
    );
  }
  if (state.added) {
    return (
      <Typography variant="body2" component="div"><Changed tone="added">{qty}<br />{price}</Changed></Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
      {state.nameChanged && (
        <Typography variant="body2" component="div" sx={{ mb: 0.25 }}><Changed>{line.name || '–'}</Changed></Typography>
      )}
      <Typography variant="body2" component="div">{state.qtyChanged ? <Changed>{qty}</Changed> : qty}</Typography>
      <Typography variant="body2" component="div" color="text.secondary">{state.priceChanged ? <Changed>{price}</Changed> : price}</Typography>
      {state.split && (
        <Typography variant="caption" color="text.secondary">แบ่งส่ง {fmtQty(line.qty)}/{fmtQty(state.sourceQty)}</Typography>
      )}
      {fromDoc && <Typography variant="caption" color="text.disabled">มาจาก {fromDoc}</Typography>}
    </Box>
  );
}

function CompareCell({ entries, visible }) {
  if (!entries) return <Typography variant="body2" sx={{ color: 'grey.400' }}>–</Typography>;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      {entries.map(e => (
        <LineValue
          key={e.line.id}
          line={e.line}
          state={e.state}
          fromDoc={visible && e.line.source && !visible.has(e.line.source.doc) ? e.line.source.doc : null}
        />
      ))}
    </Box>
  );
}

function DocCompareDialog({ open, onClose, docs }) {
  const fullScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const [bills, setBills] = useState(null);   // { details: {docNo: [...]}, headers: {docNo: {...}} }
  const [error, setError] = useState(false);
  const [onlyDiff, setOnlyDiff] = useState(true);
  const docKey = docs.map(d => d.doc_no).join(',');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setBills(null);
    setError(false);
    Promise.all(docs.map(d =>
      api.get(`/api/orders/${encodeURIComponent(d.doc_no)}`).then(({ data }) => [d.doc_no, data || {}])
    ))
      .then(entries => {
        if (cancelled) return;
        setBills({
          details: Object.fromEntries(entries.map(([no, b]) => [no, b.details || []])),
          headers: Object.fromEntries(entries.map(([no, b]) => [no, b.header || null])),
        });
      })
      .catch(err => {
        console.error('doc compare fetch error:', err);
        if (!cancelled) setError(true);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, docKey]);

  const comparison = useMemo(
    () => (bills ? buildComparison(docs, bills.details, bills.headers) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bills, docKey]
  );
  // a trail that splits/merges is shown one path per tab, so each tab reads left → right
  const paths = useMemo(() => (comparison ? buildPaths(docs, comparison.parentsOf) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [comparison]);
  const [pathIdx, setPathIdx] = useState(0);
  useEffect(() => { setPathIdx(0); }, [docKey]);
  const views = useMemo(() => (comparison ? paths.map(p => viewOf(comparison, docs, p)) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [comparison, paths]);
  const view = views[Math.min(pathIdx, views.length - 1)] || null;
  const vdocs = view ? view.vdocs : docs;
  const diffCount = view ? view.rows.filter(r => r.differs).length : 0;
  const headerDiffCount = view ? view.headerRows.filter(r => r.differs).length : 0;
  const shownRows = view ? view.rows.filter(r => !onlyDiff || r.differs) : [];
  const shownHeaderRows = view ? view.headerRows.filter(r => !onlyDiff || r.differs) : [];
  const colCount = vdocs.length + 1;
  // label of a row that changed somewhere along the trail — same orange as the changed values
  const changedLabelSx = { color: DIFF.changed.color, fontWeight: 700 };
  const ITEM_LABEL_SX = {
    same: {},
    changed: changedLabelSx,
    added: { color: DIFF.added.color, fontWeight: 700 },
    removed: { ...struck, fontWeight: 700 },
  };
  // bills each column was compared with (header caption)
  const sourcesOf = comparison ? comparison.parentsOf : {};

  // Section rows (ข้อมูลหัวบิล / รายการสินค้า) stick right under the sticky column header,
  // so they need its height
  const headRef = useRef(null);
  const [headH, setHeadH] = useState(0);
  useLayoutEffect(() => {
    const el = headRef.current;
    if (!el) return;
    const update = () => setHeadH(el.offsetHeight);
    update();
    // อัปเดตในเฟรมถัดไป กันเตือน "ResizeObserver loop completed with undelivered notifications"
    let frame = 0;
    const ro = new ResizeObserver(() => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); });
    ro.observe(el);
    return () => { cancelAnimationFrame(frame); ro.disconnect(); };
  }, [comparison]);

  const sectionRow = (title, count, total) => (
    <TableRow>
      <TableCell colSpan={colCount} sx={{ position: 'sticky', top: headH, zIndex: 1, bgcolor: 'grey.50', pt: '20px !important', pb: '8px !important', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
          {count
            ? <Pill tone="changed">ต่างกัน {count} จาก {total}</Pill>
            : <Pill tone="same">ตรงกันทั้งหมด {total}</Pill>}
        </Box>
      </TableCell>
    </TableRow>
  );
  const allSameRow = (text) => (
    <TableRow>
      <TableCell colSpan={colCount} sx={{ color: 'text.secondary' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <CheckIcon sx={{ fontSize: 18 }} /> {text}
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={vdocs.length > 3 ? 'lg' : 'md'}
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>เปรียบเทียบเส้นทางเอกสาร</Typography>
          <Typography variant="body2" color="text.secondary">แต่ละบิลเทียบกับบิลต้นทางที่อ้างอิง</Typography>
        </Box>
        <FormControlLabel
          sx={{ mr: 0.5 }}
          control={<Switch size="small" checked={onlyDiff} onChange={e => setOnlyDiff(e.target.checked)} />}
          label={<Typography variant="body2">แสดงเฉพาะที่ต่างกัน</Typography>}
        />
        <IconButton onClick={onClose} sx={{ mr: -1 }}><CloseIcon /></IconButton>
      </DialogTitle>

      {views.length > 1 && (
        <Box sx={{ display: 'flex', gap: 1, px: 3, pb: 1.5, overflowX: 'auto' }}>
          {views.map((v, i) => {
            const active = i === pathIdx;
            const n = v.headerRows.filter(r => r.differs).length + v.rows.filter(r => r.differs).length;
            return (
              <Box
                key={i}
                onClick={() => setPathIdx(i)}
                sx={{
                  flex: '0 0 auto', cursor: 'pointer', px: 1.5, py: 0.75, borderRadius: 2,
                  border: '1px solid', borderColor: active ? 'primary.main' : 'divider',
                  bgcolor: active ? '#f5f9ff' : 'background.paper',
                  boxShadow: active ? '0 0 0 1px #1976d2' : 'none',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={700}>สายที่ {i + 1}</Typography>
                  {n
                    ? <Pill tone="changed">ต่างกัน {n}</Pill>
                    : <Pill tone="same">ตรงกัน</Pill>}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {v.vdocs.map(d => d.doc_no).join(' → ')}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderTop: '1px solid', borderColor: 'divider' }}>
        {error ? (
          <Typography color="error" sx={{ p: 3 }}>โหลดข้อมูลบิลไม่สำเร็จ</Typography>
        ) : !comparison || !view ? (
          <Typography color="text.secondary" sx={{ p: 3 }}>กำลังโหลดข้อมูลบิล...</Typography>
        ) : (
          <>
            {/* the container itself scrolls, so the column header and section rows stick inside it */}
            <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Table stickyHeader sx={{
                tableLayout: 'fixed',
                '& td, & th': { py: 1.5, px: 2, borderColor: 'divider' },
                '& td:first-of-type, & th:first-of-type': { pl: 3 },
                '& td:last-of-type, & th:last-of-type': { pr: 3 },
              }}>
                <colgroup>
                  <col style={{ width: vdocs.length > 3 ? '22%' : '30%' }} />
                  {vdocs.map(d => <col key={d.doc_no} />)}
                </colgroup>
                <TableHead ref={headRef}>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'background.paper', verticalAlign: 'bottom' }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={600}>รายการ</Typography>
                    </TableCell>
                    {vdocs.map(doc => {
                      const cfg = TRAIL_STATUS[doc.status];
                      return (
                        <TableCell key={doc.doc_no} align="center" sx={{ bgcolor: 'background.paper' }}>
                          <Box component="span" sx={{
                            display: 'inline-block', px: 1.25, borderRadius: 10, fontSize: 12, fontWeight: 700, lineHeight: '22px',
                            color: cfg.color, bgcolor: cfg.bg,
                          }}>
                            {cfg.label}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, mt: 0.75 }}>
                            <Typography variant="body1" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>{doc.doc_no}</Typography>
                            <CopyDocNoButton docNo={doc.doc_no} size={15} />
                          </Box>
                          <Typography variant="body2" color="text.secondary">{fmtDate(doc.doc_date)}</Typography>
                          {sourcesOf[doc.doc_no]?.length > 0 && (
                            <Typography variant="caption" component="div" color="text.disabled" sx={{ mt: 0.25 }}>
                              เทียบกับ {sourcesOf[doc.doc_no].join(', ')}
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sectionRow('ข้อมูลหัวบิล', headerDiffCount, view.headerRows.length)}
                  {shownHeaderRows.length === 0 ? allSameRow('หัวบิลตรงกันทุกบิล') : shownHeaderRows.map(r => (
                    <TableRow key={r.label}>
                      <TableCell>
                        <Typography variant="body2" sx={r.differs ? changedLabelSx : { color: 'text.secondary' }}>{r.label}</Typography>
                      </TableCell>
                      {r.values.map((v, i) => (
                        <TableCell key={vdocs[i].doc_no} align="center">
                          <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
                            {r.valueChanged[i] ? <Changed>{v || '–'}</Changed> : (v || <Box component="span" sx={{ color: 'grey.400' }}>–</Box>)}
                          </Typography>
                          {r.links[i] ? (
                            <Box sx={{ mt: 0.25 }}>
                              {r.subChanged[i]
                                ? <Changed><MapLink url={r.links[i]} label={r.subs[i] || 'ค้นหาในแผนที่'} /></Changed>
                                : <MapLink url={r.links[i]} label={r.subs[i] || 'ค้นหาในแผนที่'} />}
                            </Box>
                          ) : r.subs[i] ? (
                            <Typography variant="caption" component="div" color="text.disabled" sx={{ mt: 0.25 }}>
                              {r.subChanged[i] ? <Changed>{r.subs[i]}</Changed> : r.subs[i]}
                            </Typography>
                          ) : r.subChanged[i] && (
                            <Box sx={{ mt: 0.25 }}><Changed><Typography variant="caption">ไม่มีพิกัด</Typography></Changed></Box>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}

                </TableBody>
                <TableBody>
                  {sectionRow('รายการสินค้า', diffCount, view.rows.length)}
                  {shownRows.length === 0 ? allSameRow('รายการสินค้าตรงกันทุกบิล') : shownRows.map(r => (
                    <TableRow key={r.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word', fontWeight: 500, ...ITEM_LABEL_SX[r.kind] }}>{r.item_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.item_code}</Typography>
                      </TableCell>
                      {vdocs.map(doc => (
                        <TableCell key={doc.doc_no} align="center">
                          <CompareCell entries={r.cells[doc.doc_no]} visible={paths.length > 1 ? view.visible : null} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow sx={{ '& td': { borderBottom: 'none', borderTop: '1px solid', borderTopColor: 'divider', bgcolor: 'grey.50', color: 'text.primary' } }}>
                    <TableCell><Typography variant="subtitle2" fontWeight={700}>ยอดรวมบิล</Typography></TableCell>
                    {vdocs.map(doc => {
                      const t = comparison.totals[doc.doc_no];
                      return (
                        <TableCell key={doc.doc_no} align="center">
                          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.4 }}>{fmt2(doc.total_amount)}</Typography>
                          <StageDiff result={t} />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function DocCompareButton({ rows, size = 'small' }) {
  const [open, setOpen] = useState(false);
  if (!rows || rows.length < 2) return null;
  return (
    <>
      <Button
        size={size}
        variant="outlined"
        startIcon={<CompareArrowsIcon />}
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderRadius: 5, px: 1.5, py: 0.25, fontWeight: 600 }}
      >
        เปรียบเทียบ
      </Button>
      <DocCompareDialog open={open} onClose={() => setOpen(false)} docs={rows} />
    </>
  );
}
